import { groupBy } from 'es-toolkit';

import { isUserVisibleException, ReadFile } from '@paragon/client-tools';
import { BearingEnvelope, TrussEnvelope } from '@paragon/contract-library';
import { designService, SegmentForLevel } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { notNullish } from '@paragon/utilities';

import { extractLayoutElementsFromCrowdBuildJson } from '../../../library/crowdBuild/importJson';
import { calculateAutomaticRunDimensions } from '../../../library/layout-geometry/run-dimensioning';
import { getOffsetBearingEnvelopeDimension } from '../../../three/layout/bearing-envelopes/BearingEnvelopeDimension';
import { getDimensionedAreaForBearingEnvelopes } from '../../selectors/getDimensionedAreaForLayout';
import { getProjectLayoutModel } from '../../selectors/getProjectLayoutModel';
import { getActiveLayoutLevelGuidOrNull, getAllTrussEnvelopes } from '../../selectors/layoutDataSelectors';
import { anyLayoutItemsExist } from '../../selectors/layoutModeSelectors';
import { layoutSlice } from '../../slices/layoutSlice';
import { downloadSharedComponentSetFiles } from '../downloadActions';
import { showConfirmReplaceLayoutDialog } from '../popupActions';
import { receiveActiveProjectSynchronizationPayload, uploadProject } from '../projectActions';
import { AsyncThunkActionVoid } from '../thunkActions';
import { saveBearingEnvelopes } from './bearingEnvelopeActions';
import { insertWorkLinesFromDxf } from './workLineActions';

const ENVELOPE_HAS_NO_LEVEL_KEY = 'no level';

export const downloadLayoutAsDxf =
  (selectedLevelGuid?: string, nameOverride?: string) => (): AsyncThunkActionVoid => async (dispatch, getState) => {
    const state = getState();
    const { openContext } = state.application;
    const automaticDimensionSegments = calculateLayoutAutomaticDimensions(
      selectedLevelGuid ?? null,
      Object.values(state.layout.trussEnvelopes),
      Object.values(state.layout.bearingEnvelopes),
    );

    switch (openContext?.type) {
      case 'shared-component-set':
        dispatch(
          downloadSharedComponentSetFiles({
            fileType: 'LayoutDxf',
            sharedComponentSetLookupCode: openContext.sharedComponentSetLookupCode,
            selectedLevelGuid,
            nameOverride,
            automaticDimensionSegments,
          }),
        );
        return;

      case 'project':
        break; // the expected case

      default:
        dispatch(enqueueError('Cannot get layout data outside of a project context.'));
        return;
    }

    const { projectGuid } = openContext;
    const projectName = nameOverride ?? getProjectLayoutModel(state)?.name ?? 'layout';

    const spinnerId = dispatch(addSpinnerMessage('Generating DXF file of layout...'));
    try {
      const dxfResult = await designService.projects.getLayoutDxfFiles(
        projectGuid,
        automaticDimensionSegments,
        selectedLevelGuid,
        nameOverride,
      );
      dxfResult.download(`${projectName}.zip`);
    } catch {
      dispatch(enqueueError('DXF generation failed.'));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export function calculateLayoutAutomaticDimensions(
  selectedLevelGuid: string | null,
  allTrussEnvelopes: TrussEnvelope[],
  allBearingEnvelopes: BearingEnvelope[],
): SegmentForLevel[] {
  const automaticDimensionSegments: SegmentForLevel[] = [];

  const trussEnvelopes = allTrussEnvelopes.filter(
    ({ levelGuid }) => !selectedLevelGuid || levelGuid === selectedLevelGuid,
  );
  const bearingEnvelopes = allBearingEnvelopes.filter(
    ({ levelGuid }) => !selectedLevelGuid || levelGuid === selectedLevelGuid,
  );

  const envelopesByLevel = groupBy(trussEnvelopes, (envelope) => envelope.levelGuid ?? ENVELOPE_HAS_NO_LEVEL_KEY);

  for (const [levelKey, levelEnvelopes] of Object.entries(envelopesByLevel)) {
    const levelGuid = levelKey === ENVELOPE_HAS_NO_LEVEL_KEY ? null : levelKey;
    const levelBearingEnvelopes = bearingEnvelopes.filter((bearingEnvelope) => bearingEnvelope.levelGuid === levelGuid);

    const dimensionedArea = getDimensionedAreaForBearingEnvelopes(
      levelBearingEnvelopes.length > 0 ? levelBearingEnvelopes : bearingEnvelopes,
    );

    const runDimensions = calculateAutomaticRunDimensions(dimensionedArea, levelEnvelopes);
    for (const segment of runDimensions) {
      automaticDimensionSegments.push({ levelGuid, segment });
    }

    const bearingDimensions = levelBearingEnvelopes.map(getOffsetBearingEnvelopeDimension).filter(notNullish);
    for (const segment of bearingDimensions) {
      automaticDimensionSegments.push({ levelGuid, segment });
    }
  }

  return automaticDimensionSegments;
}

export const uploadLayout =
  (fileContents: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;

    if (openContext?.type !== 'project') {
      dispatch(enqueueError('Cannot upload layout data outside of a project context.'));
      return;
    }

    const { projectGuid } = openContext;

    const spinnerId = dispatch(addSpinnerMessage('Uploading layout...'));

    try {
      await designService.projects.uploadLayoutFile(projectGuid, fileContents);
      const layout = await designService.projects.getLayout(projectGuid);

      dispatch(layoutSlice.actions.layoutDataLoaded(layout));
    } catch (error) {
      dispatch(enqueueError('Error uploading layout.'));
      console.error(error);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

/**
 * Imports a PPF or layout JSON file into the open project, asking the user for confirmation first if the project
 *   already contains layout elements (both file types replace the project's existing layout).
 */
export const importProjectDataFile =
  (file: ReadFile, { layoutOnly }: { layoutOnly: boolean } = { layoutOnly: false }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    if (anyLayoutItemsExist(getState())) {
      dispatch(showConfirmReplaceLayoutDialog({ file, layoutOnly }));
    } else {
      await dispatch(importProjectDataFileWithoutConfirmation(file, layoutOnly));
    }
  };

export const importProjectDataFileWithoutConfirmation =
  (file: ReadFile, layoutOnly: boolean): AsyncThunkActionVoid =>
  async (dispatch) => {
    switch (file.filetype.toLowerCase()) {
      case 'ppf':
        await dispatch(uploadProject(file, layoutOnly));
        break;
      case 'json':
        await dispatch(uploadLayout(atob(file.contents.split(',')[1])));
        break;
      default:
        throw new Error(`Invalid file type: ${file.filetype}.`);
    }
  };

export const importCrowdBuildJson =
  (fileContents: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;

    if (openContext?.type !== 'project') {
      dispatch(enqueueError('Cannot upload layout data outside of a project context.'));
      return;
    }

    const activeLevelGuid = getActiveLayoutLevelGuidOrNull(getState());

    const spinnerId = dispatch(addSpinnerMessage('Importing CrowdBuild JSON...'));

    try {
      const extractedLayoutData = extractLayoutElementsFromCrowdBuildJson(fileContents, activeLevelGuid);
      dispatch(
        layoutSlice.actions.bearingEnvelopesUpserted({ bearingEnvelopes: extractedLayoutData.bearingEnvelopes }),
      );
      await dispatch(saveBearingEnvelopes());
    } catch (error) {
      dispatch(enqueueError('Error importing CrowdBuild JSON.'));
      console.error(error);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const uploadLayoutDxf =
  (file: ReadFile): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;

    if (openContext?.type !== 'project') {
      dispatch(enqueueError('Cannot upload layout data outside of a project context.'));
      return;
    }

    const { projectGuid } = openContext;
    const spinnerId = dispatch(addSpinnerMessage('Uploading layout DXF...'));

    let segments;
    try {
      segments = await designService.projects.uploadLayoutDxfFile(projectGuid, file);
    } catch (error) {
      dispatch(enqueueError('Error uploading layout DXF.'));
      console.error(error);
      return;
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }

    await dispatch(insertWorkLinesFromDxf(segments));
  };

export const optimizeFeaturePatternContainerLayout =
  (featurePatternContainerGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { openContext } = getState().application;

    if (openContext?.type !== 'project') {
      dispatch(enqueueError('Cannot optimize container layout outside of a project context.'));
      return;
    }

    const { projectGuid } = openContext;

    const containsEnvelopes = getAllTrussEnvelopes(getState()).some(
      (envelope) => envelope.featurePatternContainerGuid === featurePatternContainerGuid,
    );

    if (containsEnvelopes) {
      dispatch(enqueueError('Optimization must start from a container with no envelopes.'));
      return;
    }

    const spinnerId = dispatch(addSpinnerMessage('Optimizing Container Layout...'));

    try {
      const { createdTrussEnvelopes, synchronizationPayload } =
        await designService.projects.optimizeFeaturePatternContainerLayout(projectGuid, featurePatternContainerGuid);
      dispatch(layoutSlice.actions.trussEnvelopesUpserted(createdTrussEnvelopes));
      dispatch(receiveActiveProjectSynchronizationPayload(synchronizationPayload));
    } catch (error) {
      const errorMessage = isUserVisibleException(error)
        ? error.userVisibleMessage
        : 'Error optimizing container layout.';
      dispatch(enqueueError(errorMessage));
      console.error(error);
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
    }
  };

export const generateEnvelopesForRoofLayout = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const { openContext } = getState().application;

  if (openContext?.type !== 'project') {
    dispatch(enqueueError('Cannot optimize container layout outside of a project context.'));
    return;
  }

  const { projectGuid } = openContext;

  const containsEnvelopes = getAllTrussEnvelopes(getState()).length > 0;

  if (containsEnvelopes) {
    dispatch(enqueueError('Auto-layout must start from a layout with no truss envelopes.'));
    return;
  }

  const spinnerId = dispatch(addSpinnerMessage('Generating envelopes...'));

  try {
    const { createdTrussEnvelopes, synchronizationPayload } =
      await designService.projects.generateTrussEnvelopes(projectGuid);
    dispatch(layoutSlice.actions.trussEnvelopesUpserted(createdTrussEnvelopes));
    dispatch(receiveActiveProjectSynchronizationPayload(synchronizationPayload));
  } catch (error) {
    const errorMessage = isUserVisibleException(error) ? error.userVisibleMessage : 'Error generating envelopes.';
    dispatch(enqueueError(errorMessage));
    console.error(error);
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};
